import { createFileRoute, notFound } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import { toast } from "sonner";
import {
  getPublicListing,
  getPublicQueue,
  joinQueueAuth,
  joinQueueGuest,
  getCanalAccess,
  saveMyWhatsapp,
  registerListingView,
} from "@/lib/garimpo-listings.functions";
import { gradeLabel } from "@/lib/vinyl-grades";
import { useCurrentUser } from "@/hooks/useCurrentUser";

import { Navbar1 } from "@/components/ui/navbar-1";
import { Loader2, ChevronLeft, ChevronRight, X, ZoomIn, Radio, Users } from "lucide-react";


export const Route = createFileRoute("/p/$code")({
  loader: async ({ params, context }) => {
    const data = await context.queryClient.ensureQueryData({
      queryKey: ["public-listing", params.code],
      queryFn: () => getPublicListing({ data: { code: params.code } }),
    });
    if (!data) throw notFound();
    return data;
  },
  head: ({ loaderData }) => {
    if (!loaderData) return { meta: [{ title: "Anúncio — Viny Club" }] };
    const l = loaderData.listing as any;
    const title = `${l.artist} — ${l.title} · R$ ${Number(l.price_brl).toFixed(0)} | Viny Club`;
    const parts = [
      l.media_grade ? `Mídia ${gradeLabel(l.media_grade)}` : "",
      l.sleeve_grade ? `Capa ${gradeLabel(l.sleeve_grade)}` : "",
      l.year ? `${l.year}` : "",
      `R$ ${Number(l.price_brl).toFixed(0)}`,
    ].filter(Boolean);
    const desc = `${parts.join(" · ")}. Entre na fila do Canal de Garimpo da Viny Club e garanta sua posição.`;
    const image = loaderData.photos?.[0]?.url || l.cover_url || "";
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:site_name", content: "Viny Club" },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:type", content: "product" },
        ...(image
          ? [
              { property: "og:image", content: image },
              { property: "og:image:width", content: "1200" },
              { property: "og:image:height", content: "1200" },
              { property: "og:image:alt", content: `${l.artist} — ${l.title}` },
              { name: "twitter:image", content: image },
            ]
          : []),
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: desc },
      ],
    };
  },
  notFoundComponent: () => (
    <div className="mx-auto max-w-md p-10 text-center">
      <p className="text-lg font-semibold">Anúncio não encontrado</p>
      <p className="text-sm text-muted-foreground">Este link pode ter expirado.</p>
    </div>
  ),
  errorComponent: ({ error }) => (
    <div className="mx-auto max-w-md p-10 text-center text-sm text-muted-foreground">
      {(error as Error).message}
    </div>
  ),
  component: PublicListingPage,
});

function PublicListingPage() {
  const { code } = Route.useParams();
  const fn = useServerFn(getPublicListing);
  const { data } = useQuery({
    queryKey: ["public-listing", code],
    queryFn: () => fn({ data: { code } }),
    refetchInterval: 20000,
  });
  const { isAuthenticated } = useCurrentUser();
  const [modalOpen, setModalOpen] = useState(false);
  const [result, setResult] = useState<{ position: number } | null>(null);
  const viewFn = useServerFn(registerListingView);

  // Count one view per listing per browser session.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const key = `viny:listing-view:${code}`;
    try {
      if (window.sessionStorage.getItem(key)) return;
      window.sessionStorage.setItem(key, "1");
    } catch {
      /* storage blocked — still count once per mount */
    }
    void viewFn({ data: { code } }).catch(() => {});
  }, [code, viewFn]);



  if (!data) return null;
  const { listing, photos, seller } = data;
  const isClosed = listing.status === "sold" || listing.status === "archived";
  const isReserved = listing.status === "reserved";

  const images = (photos.length ? photos.map((p) => p.url) : [listing.cover_url || ""]).filter(
    Boolean,
  ) as string[];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar1 mobileAccount />
      <div className="mx-auto max-w-5xl px-4 pb-32 pt-20 lg:pb-16 lg:pt-28">
        <div className="mb-5 flex items-center justify-center gap-2">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1 text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
            <Radio className="h-3 w-3 text-primary" /> Canal de Garimpo
          </span>
        </div>

        <div className="mx-auto grid max-w-md gap-6 lg:max-w-none lg:grid-cols-[300px_minmax(0,1fr)] lg:items-start lg:gap-10">
          <div className="lg:sticky lg:top-28">
            <div className="mx-auto w-full max-w-[280px] lg:max-w-none">
              <PhotoCarousel images={images} alt={`${listing.artist} — ${listing.title}`} />
            </div>
          </div>

          <div className="min-w-0">
            <div className="rounded-2xl border border-border bg-card p-5">
              <h1 className="font-display text-xl font-bold leading-tight lg:text-3xl">
                {listing.title}
              </h1>
              <p className="text-base text-muted-foreground lg:text-lg">{listing.artist}</p>

              <div className="mt-4 flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <p className="font-display text-3xl font-bold text-primary lg:text-4xl">
                  R$ {Number(listing.price_brl).toFixed(0)}
                </p>
                {(listing as any).price_pix_brl ? (
                  <span className="rounded-full border border-border bg-background px-2.5 py-1 text-xs font-medium">
                    Pix R$ {Number((listing as any).price_pix_brl).toFixed(0)}
                  </span>
                ) : null}
                {listing.year ? (
                  <span className="text-xs text-muted-foreground">{listing.year}</span>
                ) : null}
              </div>

              <div className="mt-4 grid gap-2 border-t border-border pt-4 text-sm sm:grid-cols-2">
                <Row k="Mídia" v={gradeLabel(listing.media_grade)} />
                <Row k="Capa" v={gradeLabel(listing.sleeve_grade)} />
                {listing.pressing ? <Row k="Prensagem" v={listing.pressing} /> : null}
                {listing.shipping ? <Row k="Envio" v={listing.shipping} /> : null}
                {listing.category ? <Row k="Categoria" v={listing.category} /> : null}
              </div>

              {listing.description ? (
                <p className="mt-4 whitespace-pre-wrap border-t border-border pt-4 text-sm leading-relaxed text-muted-foreground">
                  {listing.description}
                </p>
              ) : null}

              {listing.notes ? (
                <p className="mt-3 whitespace-pre-wrap text-sm text-muted-foreground">
                  {listing.notes}
                </p>
              ) : null}
            </div>

            {seller ? (
              <div className="mt-4 flex items-center gap-3 rounded-2xl border border-border bg-card p-3">
                {seller.avatar_url ? (
                  <img loading="lazy" decoding="async"
                    src={seller.avatar_url}
                    alt=""
                    className="h-10 w-10 rounded-full object-cover"
                  />
                ) : (
                  <div className="h-10 w-10 rounded-full bg-muted" />
                )}
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{seller.display_name || seller.username}</p>
                  <a
                    href={`/profile/${seller.username}`}
                    className="text-xs text-muted-foreground hover:underline"
                  >
                    Ver perfil do vendedor →
                  </a>
                </div>
              </div>
            ) : null}

            {/* Desktop CTA (inline) */}
            <div className="mt-5 hidden lg:block">
              <CtaBlock
                isClosed={isClosed}
                isReserved={isReserved}
                status={listing.status}
                result={result}
                onOpen={() => setModalOpen(true)}
              />
            </div>

            <QueueRanking code={code} myPosition={result?.position ?? null} />
          </div>
        </div>
      </div>


      {/* Fixed bottom CTA (mobile) */}
      <div className="fixed inset-x-0 bottom-0 border-t border-border bg-background/95 p-4 backdrop-blur lg:hidden">
        <div className="mx-auto max-w-lg">
          <CtaBlock
            isClosed={isClosed}
            isReserved={isReserved}
            status={listing.status}
            result={result}
            onOpen={() => setModalOpen(true)}
          />
        </div>
      </div>



      {modalOpen ? (
        <JoinModal
          code={code}
          isAuthenticated={isAuthenticated}
          onClose={() => setModalOpen(false)}
          onJoined={(r) => {
            setResult(r);
            setModalOpen(false);
          }}
        />
      ) : null}
    </div>
  );
}

function CtaBlock({
  isClosed,
  isReserved,
  status,
  result,
  onOpen,
}: {
  isClosed: boolean;
  isReserved: boolean;
  status: string;
  result: { position: number } | null;
  onOpen: () => void;
}) {
  if (isClosed) {
    return (
      <button
        disabled
        className="w-full rounded-lg bg-muted py-4 text-center text-lg font-bold text-muted-foreground"
      >
        {status === "sold" ? "Vendido" : "Encerrado"}
      </button>
    );
  }
  return (
    <>
      <button
        onClick={onOpen}
        disabled={!!result}
        className="w-full rounded-lg bg-primary py-4 text-center text-lg font-bold text-primary-foreground shadow-lg transition-transform active:scale-[0.98] disabled:opacity-60"
      >
        {result ? `Você está em #${result.position}` : "🙋 EU QUERO"}
      </button>
      <p className="mt-2 text-center text-xs text-muted-foreground">
        {result
          ? "Veja a fila completa abaixo."
          : isReserved
            ? "Reservado no momento — entre na fila de espera."
            : "Clique para entrar na fila e ver sua posição."}
      </p>
    </>
  );
}



function QueueRanking({ code, myPosition }: { code: string; myPosition: number | null }) {
  const fn = useServerFn(getPublicQueue);
  const { data: rows } = useQuery({
    queryKey: ["public-queue", code],
    queryFn: () => fn({ data: { code } }),
    refetchInterval: 20000,
  });
  const list = rows ?? [];

  return (
    <div className="mt-5 overflow-hidden rounded-2xl border border-border bg-card">
      {myPosition ? (
        <div className="border-b border-border bg-primary/10 px-4 py-3 text-center">
          <p className="font-semibold">✔️ Você é o #{myPosition} da fila</p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            O vendedor chama pelo WhatsApp quando for sua vez.
          </p>
        </div>
      ) : null}

      <div className="flex items-center justify-between gap-2 border-b border-border px-4 py-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold">Ranking por ordem de chegada</p>
          <p className="text-[11px] text-muted-foreground">
            Quem clicou primeiro em “Eu quero” tem prioridade.
          </p>
        </div>
        <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-background px-2.5 py-1 text-xs font-semibold">
          <Users className="h-3 w-3" /> {list.length}
        </span>
      </div>

      {list.length === 0 ? (
        <p className="px-4 py-6 text-center text-sm text-muted-foreground">
          Ninguém na fila ainda — seja o primeiro.
        </p>
      ) : (
        <ul className="divide-y divide-border">
          {list.map((r) => (
            <li
              key={r.id}
              className={`flex items-center gap-3 px-4 py-2.5 text-sm ${
                myPosition && r.rank === myPosition ? "bg-primary/10 font-semibold" : ""
              }`}
            >
              <span className="w-7 text-center">
                {r.rank === 1 ? "🥇" : r.rank === 2 ? "🥈" : r.rank === 3 ? "🥉" : `#${r.rank}`}
              </span>
              <span className="min-w-0 flex-1 truncate">{r.name}</span>
              {r.status === "reserved" ? (
                <span className="shrink-0 rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-600">
                  reservado
                </span>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </div>
  );

}


function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex justify-between">
      <span className="text-muted-foreground">{k}</span>
      <span className="font-medium">{v}</span>
    </div>
  );
}

function JoinModal({
  code,
  isAuthenticated,
  onClose,
  onJoined,
}: {
  code: string;
  isAuthenticated: boolean;
  onClose: () => void;
  onJoined: (r: { position: number }) => void;
}) {
  const authFn = useServerFn(joinQueueAuth);
  const guestFn = useServerFn(joinQueueGuest);
  const accessFn = useServerFn(getCanalAccess);
  const saveWppFn = useServerFn(saveMyWhatsapp);
  const [name, setName] = useState("");
  const [wpp, setWpp] = useState("");

  const accessQ = useQuery({
    queryKey: ["canal", "access"],
    queryFn: () => accessFn(),
    enabled: isAuthenticated,
    staleTime: 60_000,
  });

  // Logged in but no WhatsApp on file → collect it once.
  const needsWpp = isAuthenticated && !accessQ.isLoading && !accessQ.data?.whatsapp;

  const mut = useMutation({
    mutationFn: async () => {
      if (isAuthenticated) {
        if (needsWpp) await saveWppFn({ data: { whatsapp: wpp } });
        return authFn({ data: { code } });
      }
      return guestFn({ data: { code, name, whatsapp: wpp } });
    },
    onSuccess: (r) => {
      toast.success(r.alreadyIn ? "Você já estava na fila!" : "Posição garantida!");
      onJoined({ position: r.position });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const disabled =
    mut.isPending ||
    (!isAuthenticated && (!name || !wpp)) ||
    (needsWpp && wpp.replace(/\D/g, "").length < 10);

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/70 backdrop-blur-sm sm:items-center"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-t-2xl bg-card p-6 pb-[calc(1.5rem+env(safe-area-inset-bottom))] sm:rounded-2xl sm:pb-6"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="font-display text-xl font-bold">Entrar na fila</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          {!isAuthenticated
            ? "Deixe seu contato — o vendedor te chama quando for sua vez."
            : needsWpp
              ? "Só falta o seu WhatsApp para o vendedor te chamar. Guardamos no seu perfil e não pedimos de novo."
              : "Clique para confirmar sua posição."}
        </p>

        {!isAuthenticated ? (
          <div className="mt-4 space-y-3">
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-muted-foreground">
                Seu nome
              </span>
              <input
                autoFocus
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                placeholder="João Silva"
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-muted-foreground">
                WhatsApp
              </span>
              <input
                inputMode="numeric"
                value={wpp}
                onChange={(e) => setWpp(e.target.value)}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                placeholder="11999998888"
              />
            </label>
          </div>
        ) : needsWpp ? (
          <label className="mt-4 block">
            <span className="mb-1 block text-xs font-medium text-muted-foreground">
              Seu WhatsApp
            </span>
            <input
              autoFocus
              inputMode="numeric"
              value={wpp}
              onChange={(e) => setWpp(e.target.value)}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              placeholder="11999998888"
            />
          </label>
        ) : null}

        <button
          onClick={() => mut.mutate()}
          disabled={disabled}
          className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-primary py-3 text-base font-bold text-primary-foreground disabled:opacity-60"
        >
          {mut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          🙋 Quero este disco
        </button>
        <button
          onClick={onClose}
          className="mt-2 w-full py-2 text-sm text-muted-foreground hover:text-foreground"
        >
          Cancelar
        </button>
      </div>
    </div>
  );
}


function PhotoCarousel({ images, alt }: { images: string[]; alt: string }) {
  const [index, setIndex] = useState(0);
  const [zoom, setZoom] = useState(false);
  const total = images.length;
  const go = (d: number) => setIndex((i) => (i + d + total) % total);

  if (!total) return null;

  return (
    <>
      <div className="relative overflow-hidden rounded-2xl bg-muted">
        <div
          className="flex transition-transform duration-300 ease-out"
          style={{ transform: `translateX(-${index * 100}%)` }}
        >
          {images.map((src, i) => (
            <button
              key={src + i}
              type="button"
              onClick={() => setZoom(true)}
              className="w-full shrink-0 cursor-zoom-in"
              aria-label="Ampliar foto"
            >
              <img
                src={src}
                alt={alt}
                loading={i === 0 ? "eager" : "lazy"}
                className="aspect-square w-full object-cover"
              />
            </button>
          ))}
        </div>

        <span className="pointer-events-none absolute right-3 top-3 inline-flex items-center gap-1 rounded-full bg-black/60 px-2 py-1 text-[11px] text-white backdrop-blur">
          <ZoomIn className="h-3 w-3" /> Toque para ampliar
        </span>

        {total > 1 ? (
          <>
            <button
              type="button"
              onClick={() => go(-1)}
              aria-label="Foto anterior"
              className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-black/55 p-2 text-white backdrop-blur transition hover:bg-black/75"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={() => go(1)}
              aria-label="Próxima foto"
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-black/55 p-2 text-white backdrop-blur transition hover:bg-black/75"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
            <div className="absolute inset-x-0 bottom-3 flex justify-center gap-1.5">
              {images.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  aria-label={`Foto ${i + 1}`}
                  onClick={() => setIndex(i)}
                  className={`h-1.5 rounded-full transition-all ${
                    i === index ? "w-5 bg-white" : "w-1.5 bg-white/50"
                  }`}
                />
              ))}
            </div>
          </>
        ) : null}
      </div>

      {total > 1 ? (
        <div className="mt-3 flex gap-2 overflow-x-auto pb-1">
          {images.map((src, i) => (
            <button
              key={"t" + src + i}
              type="button"
              onClick={() => setIndex(i)}
              className={`h-16 w-16 shrink-0 overflow-hidden rounded-lg border transition ${
                i === index ? "border-primary" : "border-border opacity-70 hover:opacity-100"
              }`}
            >
              <img src={src} alt="" loading="lazy" className="h-full w-full object-cover" />
            </button>
          ))}
        </div>
      ) : null}

      {zoom ? (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/95 p-4"
          onClick={() => setZoom(false)}
        >
          <button
            type="button"
            aria-label="Fechar"
            onClick={() => setZoom(false)}
            className="absolute right-4 top-4 rounded-full bg-white/10 p-2 text-white"
          >
            <X className="h-5 w-5" />
          </button>
          <img loading="lazy" decoding="async"
            src={images[index]}
            alt={alt}
            onClick={(e) => e.stopPropagation()}
            className="max-h-[85vh] max-w-full rounded-xl object-contain"
          />
          {total > 1 ? (
            <>
              <button
                type="button"
                aria-label="Foto anterior"
                onClick={(e) => {
                  e.stopPropagation();
                  go(-1);
                }}
                className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full bg-white/10 p-3 text-white"
              >
                <ChevronLeft className="h-6 w-6" />
              </button>
              <button
                type="button"
                aria-label="Próxima foto"
                onClick={(e) => {
                  e.stopPropagation();
                  go(1);
                }}
                className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full bg-white/10 p-3 text-white"
              >
                <ChevronRight className="h-6 w-6" />
              </button>
            </>
          ) : null}
        </div>
      ) : null}
    </>
  );
}
